// Typed API client for anchor-api. Reads the access token from a module-level
// variable set by AuthContext. On 401, attempts one token refresh before failing.

const BASE = import.meta.env.VITE_API_URL ?? '';

// ---------------------------------------------------------------------------
// Token store — set by AuthContext, read here.
// ---------------------------------------------------------------------------

let _accessToken: string | null = null;
let _refreshToken: string | null = null;
let _onUnauthenticated: (() => void) | null = null;

export function setTokens(access: string, refresh: string) {
  _accessToken = access;
  _refreshToken = refresh;
}

export function clearTokens() {
  _accessToken = null;
  _refreshToken = null;
}

export function registerUnauthHandler(fn: () => void) {
  _onUnauthenticated = fn;
}

// ---------------------------------------------------------------------------
// Shared fetch wrapper
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`API error ${status}`);
  }
}

async function refreshSession(): Promise<boolean> {
  if (!_refreshToken) return false;
  try {
    const res = await fetch(`${BASE}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: _refreshToken }),
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { access_token: string; refresh_token: string };
    setTokens(data.access_token, data.refresh_token);
    localStorage.setItem('anchor_access', data.access_token);
    localStorage.setItem('anchor_refresh', data.refresh_token);
    return true;
  } catch {
    return false;
  }
}

async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  retry = true,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string>),
  };
  if (_accessToken) headers['Authorization'] = `Bearer ${_accessToken}`;

  const res = await fetch(`${BASE}${path}`, { ...init, headers });

  if (res.status === 401 && retry) {
    const refreshed = await refreshSession();
    if (refreshed) return apiFetch(path, init, false);
    _onUnauthenticated?.();
    throw new ApiError(401, null);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Domain types (camelCase — mapped from the snake_case backend responses)
// ---------------------------------------------------------------------------

export type OrgRole = 'owner' | 'admin' | 'member';
export type MemberStatus = 'active' | 'pending' | 'suspended';
export type JoinMethod = 'code' | 'invitation';

export interface AuthUser {
  id: string;
  displayName: string;
  email: string;
  emailIsRelay: boolean;
  orgId: string;
  orgName: string;
  orgSlug: string;
  role: OrgRole;
}

export interface JoinRequest {
  id: string;
  displayName: string;
  email: string;
  emailIsRelay: boolean;
  method: JoinMethod;
  message: string | null;
  requestedAt: string;
  status: 'pending';
}

export interface Member {
  id: string;
  displayName: string;
  email: string;
  emailIsRelay: boolean;
  role: OrgRole;
  status: MemberStatus;
  joinedAt: string;
}

export interface Group {
  id: string;
  name: string;
  description: string | null;
  teamCount: number;
  memberCount: number;
  archivedAt: string | null;
}

export interface Team {
  id: string;
  groupId: string;
  groupName: string;
  name: string;
  timezone: string;
  memberCount: number;
  archivedAt: string | null;
}

export interface TeamMember {
  userId: string;
  displayName: string;
  role: 'lead' | 'member';
  joinedAt: string;
}

export interface Question {
  id: string;
  text: string;
  purpose: string;
  principle: string;
  depth: number;
  /** R-261: 1 = casual icebreaker → 5 = deep/personal work question. */
  tone: number;
  duration: 'quick' | '5min' | '10min';
  /** 'default' = global seed bank, 'generated' = AI-produced (R-285), 'manual' = admin-curated. */
  source: 'default' | 'generated' | 'manual';
  isDefault: boolean;
  archivedAt: string | null;
}

export interface AuditEntry {
  id: string;
  actorName: string;
  action: string;
  targetType: string | null;
  targetId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface OrgInsights {
  // Core counts
  activeMembers: number;
  totalMembers: number;
  activeMembers30d: number;
  pendingApprovals: number;
  coachMessages30d: number;
  kudos30d: number;
  // R-211: org-wide engine distribution (only teams with ≥5 members contribute).
  engineDistributionSuppressed: boolean;
  engineLowPct: number | null;
  engineJustRightPct: number | null;
  engineHighPct: number | null;
  engineSharedCount: number | null;
  groupSummary: Array<{
    id: string;
    name: string;
    teamCount: number;
    memberCount: number;
  }>;
}

export interface TeamInsights {
  memberCount: number;
  /** true if memberCount < 5 — R-100 k-anonymity; no metric values should be shown. */
  suppressed: boolean;
  activeMembersThisWeek: number | null;
  kudosGiven30d: number | null;
  kudosReceived30d: number | null;
  principleMix30d: { feltSafety: number; connection: number; selfRegulation: number } | null;
  /** R-210: only shared entries; opt-out excluded from both sides. */
  engineLowPct: number | null;
  engineJustRightPct: number | null;
  engineHighPct: number | null;
  engineSharedCount: number | null;
  checkinParticipants30d: number | null;
}

export interface GroupTeamSummary {
  id: string;
  name: string;
  memberCount: number;
  suppressed: boolean;
  activeMembersThisWeek: number | null;
  kudos30d: number | null;
  checkinParticipants30d: number | null;
}

export interface GroupInsights {
  groupId: string;
  groupName: string;
  memberCount: number;
  teamCount: number;
  teams: GroupTeamSummary[];
  /** R-101: true when any team's removal would drop remaining count below 5. */
  rollupSuppressed: boolean;
  rollupActiveMembersThisWeek: number | null;
  rollupKudos30d: number | null;
  rollupCheckinParticipants30d: number | null;
  rollupEngineLowPct: number | null;
  rollupEngineJustRightPct: number | null;
  rollupEngineHighPct: number | null;
  adoptionCurve: Array<{ week: string; activeMembers: number | null }>;
}

export interface ReportedItem {
  id: string;
  feedItemId: string;
  reporterName: string;
  reason: string;
  status: 'open' | 'resolved';
  createdAt: string;
  excerpt: string;
  itemType: 'kudos' | 'answer' | 'question';
  questionIsCustom: boolean | null;
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

// R-358: 6-digit one-time-code email sign-in/sign-up (supersedes the old
// link-based /auth/verify flow).

export interface EmailAuthSession {
  accessToken: string;
  refreshToken: string;
  accessExpiresAt: string;
}

export type EmailVerifyResult =
  | ({ kind: 'success' } & EmailAuthSession)
  | { kind: 'existing_account'; provider: 'apple' | 'microsoft'; mergeToken: string }
  | { kind: 'error'; message: string };

export type EmailMergeResult = ({ kind: 'success' } & EmailAuthSession) | { kind: 'error'; message: string };

function errorCode(err: unknown): string | null {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const code = (err.body as { error?: unknown }).error;
    return typeof code === 'string' ? code : null;
  }
  return null;
}

/** `POST /v1/auth/email/start` — always `{ ok: true }` on success (R-358 rule 2: anti-enumeration). */
export async function startEmailCode(email: string): Promise<void> {
  await apiFetch('/v1/auth/email/start', {
    method: 'POST',
    body: JSON.stringify({ email, client: 'console' }),
  });
}

export async function verifyEmailCode(email: string, code: string): Promise<EmailVerifyResult> {
  try {
    const data = await apiFetch<
      | { access_token: string; refresh_token: string; access_expires_at: string }
      | { status: 'existing_account'; provider: 'apple' | 'microsoft'; merge_token: string }
    >('/v1/auth/email/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });

    if ('status' in data && data.status === 'existing_account') {
      return { kind: 'existing_account', provider: data.provider, mergeToken: data.merge_token };
    }
    if ('access_token' in data) {
      return {
        kind: 'success',
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        accessExpiresAt: data.access_expires_at,
      };
    }
    return { kind: 'error', message: 'Something went wrong. Please try again.' };
  } catch (err) {
    if (errorCode(err) === 'invalid_or_expired_token') {
      return {
        kind: 'error',
        message: 'That code is incorrect or has expired. Request a new one and try again.',
      };
    }
    return { kind: 'error', message: 'Something went wrong. Please try again.' };
  }
}

export async function mergeEmailIdentity(mergeToken: string): Promise<EmailMergeResult> {
  try {
    const data = await apiFetch<{
      access_token: string;
      refresh_token: string;
      access_expires_at: string;
    }>('/v1/auth/email/merge', {
      method: 'POST',
      body: JSON.stringify({ merge_token: mergeToken }),
    });
    return {
      kind: 'success',
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      accessExpiresAt: data.access_expires_at,
    };
  } catch {
    return {
      kind: 'error',
      message: 'That merge request has expired. Start over and enter your email again.',
    };
  }
}

// ---------------------------------------------------------------------------
// Microsoft Entra ID sign-in (R-306): browser Authorization Code + PKCE flow,
// posting to the same `/v1/auth/microsoft` endpoint iOS uses. No API changes
// required — see docs/anchor-requirements.md §4.2.
// ---------------------------------------------------------------------------

export type MicrosoftSignInResult =
  | ({ kind: 'success' } & EmailAuthSession)
  | { kind: 'error'; message: string };

export async function signInWithMicrosoft(
  code: string,
  codeVerifier: string,
  redirectUri: string,
): Promise<MicrosoftSignInResult> {
  try {
    const data = await apiFetch<{
      access_token: string;
      refresh_token: string;
      access_expires_at: string;
    }>('/v1/auth/microsoft', {
      method: 'POST',
      body: JSON.stringify({ code, code_verifier: codeVerifier, redirect_uri: redirectUri }),
    });
    return {
      kind: 'success',
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      accessExpiresAt: data.access_expires_at,
    };
  } catch (err) {
    const errCode = errorCode(err);
    if (errCode === 'microsoft_auth_not_configured') {
      return {
        kind: 'error',
        message: 'Microsoft sign-in is not available right now. Please use email instead.',
      };
    }
    if (errCode === 'microsoft_auth_failed') {
      return {
        kind: 'error',
        message: 'Microsoft sign-in failed. Please try again.',
      };
    }
    return { kind: 'error', message: 'Something went wrong. Please try again.' };
  }
}

export async function signOut(): Promise<void> {
  await apiFetch('/v1/auth/signout', { method: 'POST' }).catch(() => undefined);
  clearTokens();
  localStorage.removeItem('anchor_access');
  localStorage.removeItem('anchor_refresh');
}

interface MeResponse {
  user: { id: string; display_name: string };
  org: { id: string; name: string; slug: string } | null;
  role?: string;
}

function mapMeResponse(data: MeResponse): AuthUser {
  return {
    id: data.user.id,
    displayName: data.user.display_name,
    email: '',
    emailIsRelay: false,
    orgId: data.org?.id ?? '',
    orgName: data.org?.name ?? '',
    orgSlug: data.org?.slug ?? '',
    role: (data.role ?? 'member') as OrgRole,
  };
}

export async function getMe(): Promise<AuthUser> {
  const data = await apiFetch<MeResponse>('/v1/me');
  return mapMeResponse(data);
}

/** R-358 rule 7 / R-32: sets the display name for an account that has none yet. */
export async function updateDisplayName(name: string): Promise<AuthUser> {
  const data = await apiFetch<MeResponse>('/v1/me', {
    method: 'PATCH',
    body: JSON.stringify({ display_name: name }),
  });
  return mapMeResponse(data);
}

// ---------------------------------------------------------------------------
// Org creation (org setup wizard — R-73)
// ---------------------------------------------------------------------------

export async function createOrg(
  name: string,
  slug?: string,
): Promise<{ orgId: string; teamId: string; joinCode: string }> {
  const data = await apiFetch<{
    org_id: string;
    name: string;
    slug: string;
    join_code: string;
    team_id: string;
  }>('/v1/orgs', {
    method: 'POST',
    body: JSON.stringify({ name, slug }),
  });
  return { orgId: data.org_id, teamId: data.team_id, joinCode: data.join_code };
}

// ---------------------------------------------------------------------------
// Join requests (R-73–R-78)
// ---------------------------------------------------------------------------

export async function listJoinRequests(orgId: string): Promise<JoinRequest[]> {
  const data = await apiFetch<{
    requests: Array<{
      id: string;
      display_name: string;
      email: string | null;
      email_is_relay: number | null;
      method: string;
      message: string | null;
      created_at: string;
    }>;
  }>(`/v1/orgs/${orgId}/join-requests`);

  return data.requests.map((r) => ({
    id: r.id,
    displayName: r.display_name,
    email: r.email ?? '',
    emailIsRelay: r.email_is_relay === 1,
    method: r.method as JoinMethod,
    message: r.message,
    requestedAt: r.created_at,
    status: 'pending' as const,
  }));
}

export async function approveJoinRequest(
  orgId: string,
  requestId: string,
): Promise<void> {
  await apiFetch(`/v1/orgs/${orgId}/join-requests/${requestId}/approve`, {
    method: 'POST',
  });
}

export async function denyJoinRequest(
  orgId: string,
  requestId: string,
  reason?: string,
): Promise<void> {
  await apiFetch(`/v1/orgs/${orgId}/join-requests/${requestId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ deny_reason: reason ?? null }),
  });
}

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------

export async function listMembers(orgId: string): Promise<Member[]> {
  const members: Member[] = [];
  let cursor: string | undefined;

  do {
    const url = `/v1/orgs/${orgId}/members${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`;
    const data = await apiFetch<{
      members: Array<{
        id: string;
        display_name: string;
        email: string | null;
        email_is_relay: number | null;
        role: string;
        status: string;
        joined_at: string | null;
      }>;
      nextCursor: string | null;
    }>(url);

    members.push(
      ...data.members.map((m) => ({
        id: m.id,
        displayName: m.display_name,
        email: m.email ?? '',
        emailIsRelay: m.email_is_relay === 1,
        role: m.role as OrgRole,
        status: m.status as MemberStatus,
        joinedAt: m.joined_at ?? '',
      })),
    );

    cursor = data.nextCursor ?? undefined;
  } while (cursor);

  return members;
}

export async function updateMemberRole(
  orgId: string,
  userId: string,
  role: OrgRole,
): Promise<void> {
  await apiFetch(`/v1/orgs/${orgId}/members/${userId}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
}

export async function removeMember(orgId: string, userId: string): Promise<void> {
  await apiFetch(`/v1/orgs/${orgId}/members/${userId}/remove`, { method: 'POST' });
}

/**
 * R-34/R-146: full account deletion, initiated by an org owner. Distinct from
 * removeMember (which only sets membership status). This permanently deletes
 * the user's global account and all personal data within 30 days. Owner-only.
 */
export async function deleteUserAccount(orgId: string, userId: string): Promise<void> {
  await apiFetch(`/v1/orgs/${orgId}/members/${userId}/account`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

export async function listGroups(orgId: string): Promise<Group[]> {
  const data = await apiFetch<{
    groups: Array<{
      id: string;
      name: string;
      description: string | null;
      archived_at: string | null;
      team_count: number;
      member_count: number;
    }>;
  }>(`/v1/orgs/${orgId}/groups`);

  return data.groups.map((g) => ({
    id: g.id,
    name: g.name,
    description: g.description,
    teamCount: g.team_count,
    memberCount: g.member_count,
    archivedAt: g.archived_at,
  }));
}

export async function createGroup(
  orgId: string,
  data: { name: string; description?: string },
): Promise<Group> {
  const res = await apiFetch<{
    group_id: string;
    name: string;
  }>(`/v1/orgs/${orgId}/groups`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return {
    id: res.group_id,
    name: res.name,
    description: data.description ?? null,
    teamCount: 0,
    memberCount: 0,
    archivedAt: null,
  };
}

export async function archiveGroup(orgId: string, groupId: string): Promise<void> {
  await apiFetch(`/v1/orgs/${orgId}/groups/${groupId}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Teams
// ---------------------------------------------------------------------------

export async function listTeams(orgId: string): Promise<Team[]> {
  const data = await apiFetch<{
    teams: Array<{
      id: string;
      group_id: string;
      group_name: string;
      name: string;
      timezone: string;
      archived_at: string | null;
      member_count: number;
    }>;
  }>(`/v1/orgs/${orgId}/teams`);

  return data.teams.map((t) => ({
    id: t.id,
    groupId: t.group_id,
    groupName: t.group_name,
    name: t.name,
    timezone: t.timezone,
    memberCount: t.member_count,
    archivedAt: t.archived_at,
  }));
}

export async function listTeamMembers(orgId: string, teamId: string): Promise<TeamMember[]> {
  const data = await apiFetch<{
    members: Array<{ user_id: string; display_name: string; role: string; joined_at: string }>;
  }>(`/v1/orgs/${orgId}/teams/${teamId}/members`);
  return data.members.map((m) => ({
    userId: m.user_id,
    displayName: m.display_name,
    role: m.role as TeamMember['role'],
    joinedAt: m.joined_at,
  }));
}

export async function addTeamMember(orgId: string, teamId: string, userId: string): Promise<void> {
  await apiFetch(`/v1/orgs/${orgId}/teams/${teamId}/members`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });
}

export async function removeTeamMember(orgId: string, teamId: string, userId: string): Promise<void> {
  await apiFetch(`/v1/orgs/${orgId}/teams/${teamId}/members/${userId}`, { method: 'DELETE' });
}

export async function createTeam(
  orgId: string,
  data: { name: string; groupId: string; timezone?: string },
): Promise<{ teamId: string; name: string }> {
  const res = await apiFetch<{ team_id: string; name: string }>(
    `/v1/orgs/${orgId}/teams`,
    {
      method: 'POST',
      body: JSON.stringify({
        name: data.name,
        group_id: data.groupId,
        timezone: data.timezone ?? 'UTC',
      }),
    },
  );
  return { teamId: res.team_id, name: res.name };
}

/**
 * R-353: explicitly set/change a team's timezone (owner/admin only), which
 * governs R-294's check-in day boundary. Distinct from R-342's per-user
 * timezone/quiet-hours path — never share a route or UI control with that.
 */
export async function updateTeamTimezone(
  orgId: string,
  teamId: string,
  timezone: string,
): Promise<{ timezone: string }> {
  const res = await apiFetch<{ team_id: string; timezone: string }>(
    `/v1/orgs/${orgId}/teams/${teamId}/timezone`,
    { method: 'PATCH', body: JSON.stringify({ timezone }) },
  );
  return { timezone: res.timezone };
}

// ---------------------------------------------------------------------------
// Question bank (R-51)
// ---------------------------------------------------------------------------

export async function listQuestions(orgId: string): Promise<Question[]> {
  const data = await apiFetch<{
    questions: Array<{
      id: string;
      org_id: string | null;
      text: string;
      purpose: string;
      principle: string;
      depth: number;
      tone: number;
      duration: string;
      source: string;
      archived_at: string | null;
    }>;
  }>(`/v1/orgs/${orgId}/questions`);

  return data.questions.map((q) => ({
    id: q.id,
    text: q.text,
    purpose: q.purpose,
    principle: q.principle,
    depth: q.depth,
    tone: q.tone ?? 3,
    duration: q.duration as Question['duration'],
    source: (q.source ?? (q.org_id === null ? 'default' : 'manual')) as Question['source'],
    isDefault: q.org_id === null,
    archivedAt: q.archived_at,
  }));
}

export async function createQuestion(
  orgId: string,
  data: Omit<Question, 'id' | 'isDefault' | 'archivedAt'>,
): Promise<Question> {
  const res = await apiFetch<{
    id: string;
    org_id: string | null;
    text: string;
    purpose: string;
    principle: string;
    depth: number;
    tone: number;
    duration: string;
    source: string;
    archived_at: string | null;
  }>(`/v1/orgs/${orgId}/questions`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return {
    id: res.id,
    text: res.text,
    purpose: res.purpose,
    principle: res.principle,
    depth: res.depth,
    tone: res.tone ?? 3,
    duration: res.duration as Question['duration'],
    source: (res.source ?? 'manual') as Question['source'],
    isDefault: res.org_id === null,
    archivedAt: res.archived_at,
  };
}

export async function archiveQuestion(
  orgId: string,
  questionId: string,
): Promise<void> {
  await apiFetch(`/v1/orgs/${orgId}/questions/${questionId}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Insights (§7, R-100 k-anonymity enforced server-side)
// ---------------------------------------------------------------------------

export async function getOrgInsights(orgId: string): Promise<OrgInsights> {
  const data = await apiFetch<{
    active_members: number;
    total_members: number;
    active_members_30d: number;
    pending_approvals: number;
    coach_messages_30d: number;
    kudos_30d: number;
    engine_distribution_suppressed: boolean;
    engine_low_pct: number | null;
    engine_just_right_pct: number | null;
    engine_high_pct: number | null;
    engine_shared_count: number | null;
    group_summary: Array<{ id: string; name: string; team_count: number; member_count: number }>;
  }>(`/v1/orgs/${orgId}/insights`);

  return {
    activeMembers: data.active_members,
    totalMembers: data.total_members,
    activeMembers30d: data.active_members_30d,
    pendingApprovals: data.pending_approvals,
    coachMessages30d: data.coach_messages_30d,
    kudos30d: data.kudos_30d,
    engineDistributionSuppressed: data.engine_distribution_suppressed,
    engineLowPct: data.engine_low_pct,
    engineJustRightPct: data.engine_just_right_pct,
    engineHighPct: data.engine_high_pct,
    engineSharedCount: data.engine_shared_count,
    groupSummary: data.group_summary.map((g) => ({
      id: g.id,
      name: g.name,
      teamCount: g.team_count,
      memberCount: g.member_count,
    })),
  };
}

export async function getGroupInsights(groupId: string): Promise<GroupInsights> {
  const data = await apiFetch<{
    group_id: string;
    group_name: string;
    member_count: number;
    team_count: number;
    teams: Array<{
      id: string;
      name: string;
      member_count: number;
      suppressed: boolean;
      active_members_7d: number | null;
      kudos_30d: number | null;
      checkin_participants_30d: number | null;
    }>;
    rollup_suppressed: boolean;
    rollup_active_members_7d: number | null;
    rollup_kudos_30d: number | null;
    rollup_checkin_participants_30d: number | null;
    rollup_engine_low_pct: number | null;
    rollup_engine_just_right_pct: number | null;
    rollup_engine_high_pct: number | null;
    adoption_curve: Array<{ week: string; active_members: number | null }>;
  }>(`/v1/groups/${groupId}/insights`);

  return {
    groupId: data.group_id,
    groupName: data.group_name,
    memberCount: data.member_count,
    teamCount: data.team_count,
    teams: data.teams.map((t) => ({
      id: t.id,
      name: t.name,
      memberCount: t.member_count,
      suppressed: t.suppressed,
      activeMembersThisWeek: t.active_members_7d,
      kudos30d: t.kudos_30d,
      checkinParticipants30d: t.checkin_participants_30d,
    })),
    rollupSuppressed: data.rollup_suppressed,
    rollupActiveMembersThisWeek: data.rollup_active_members_7d,
    rollupKudos30d: data.rollup_kudos_30d,
    rollupCheckinParticipants30d: data.rollup_checkin_participants_30d,
    rollupEngineLowPct: data.rollup_engine_low_pct,
    rollupEngineJustRightPct: data.rollup_engine_just_right_pct,
    rollupEngineHighPct: data.rollup_engine_high_pct,
    adoptionCurve: data.adoption_curve.map((w) => ({ week: w.week, activeMembers: w.active_members })),
  };
}

export async function getTeamInsights(teamId: string): Promise<TeamInsights> {
  const data = await apiFetch<{
    member_count: number;
    suppressed: boolean;
    active_members_7d: number | null;
    kudos_given_30d: number | null;
    kudos_received_30d: number | null;
    principle_mix_30d: { felt_safety: number; connection: number; self_regulation: number } | null;
    engine_low_pct: number | null;
    engine_just_right_pct: number | null;
    engine_high_pct: number | null;
    engine_shared_count: number | null;
    checkin_participants_30d: number | null;
  }>(`/v1/teams/${teamId}/insights`);

  return {
    memberCount: data.member_count,
    suppressed: data.suppressed,
    activeMembersThisWeek: data.active_members_7d,
    kudosGiven30d: data.kudos_given_30d,
    kudosReceived30d: data.kudos_received_30d,
    principleMix30d: data.principle_mix_30d
      ? {
          feltSafety: data.principle_mix_30d.felt_safety,
          connection: data.principle_mix_30d.connection,
          selfRegulation: data.principle_mix_30d.self_regulation,
        }
      : null,
    engineLowPct: data.engine_low_pct,
    engineJustRightPct: data.engine_just_right_pct,
    engineHighPct: data.engine_high_pct,
    engineSharedCount: data.engine_shared_count,
    checkinParticipants30d: data.checkin_participants_30d,
  };
}

export async function triggerExport(orgId: string): Promise<{ exportId: string }> {
  const data = await apiFetch<{ export_id: string; expires_in_hours: number }>(
    `/v1/orgs/${orgId}/export`,
    { method: 'POST' },
  );
  return { exportId: data.export_id };
}

export function exportDownloadUrl(orgId: string, exportId: string): string {
  return `${BASE}/v1/orgs/${orgId}/exports/${exportId}`;
}

// ---------------------------------------------------------------------------
// Moderation (R-63)
// ---------------------------------------------------------------------------

export async function listReports(orgId: string): Promise<ReportedItem[]> {
  const reports: ReportedItem[] = [];
  let cursor: string | undefined;

  do {
    const url = `/v1/orgs/${orgId}/reports${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`;
    const data = await apiFetch<{
      reports: Array<{
        id: string;
        feed_item_id: string;
        reporter_name: string;
        reason: string;
        excerpt: string;
        status: 'open' | 'resolved';
        created_at: string;
        item_type: string;
        question_is_custom: boolean | null;
      }>;
      nextCursor: string | null;
    }>(url);

    reports.push(
      ...data.reports.map((r) => ({
        id: r.id,
        feedItemId: r.feed_item_id,
        reporterName: r.reporter_name,
        reason: r.reason,
        excerpt: r.excerpt,
        status: r.status,
        createdAt: r.created_at,
        itemType: r.item_type as ReportedItem['itemType'],
        questionIsCustom: r.question_is_custom,
      })),
    );

    cursor = data.nextCursor ?? undefined;
  } while (cursor);

  return reports;
}

export async function resolveReport(
  orgId: string,
  reportId: string,
  action?: 'archive' | 'suppress',
): Promise<void> {
  await apiFetch(`/v1/orgs/${orgId}/reports/${reportId}/resolve`, {
    method: 'POST',
    body: action ? JSON.stringify({ action }) : undefined,
  });
}

// ---------------------------------------------------------------------------
// Audit log (R-94)
// ---------------------------------------------------------------------------

export async function listAuditLog(
  orgId: string,
  cursor?: string,
): Promise<{ entries: AuditEntry[]; nextCursor: string | null }> {
  const url = `/v1/orgs/${orgId}/audit-log${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`;
  const data = await apiFetch<{
    entries: Array<{
      id: string;
      actor_name: string;
      action: string;
      target_type: string | null;
      target_id: string | null;
      metadata_json: string | null;
      created_at: string;
    }>;
    nextCursor: string | null;
  }>(url);

  return {
    entries: data.entries.map((e) => ({
      id: e.id,
      actorName: e.actor_name,
      action: e.action,
      targetType: e.target_type,
      targetId: e.target_id,
      metadata: e.metadata_json ? (JSON.parse(e.metadata_json) as Record<string, unknown>) : null,
      createdAt: e.created_at,
    })),
    nextCursor: data.nextCursor,
  };
}

// ---------------------------------------------------------------------------
// Org settings
// ---------------------------------------------------------------------------

export async function rotateJoinCode(orgId: string): Promise<{ joinCode: string }> {
  const data = await apiFetch<{ join_code: string }>(
    `/v1/orgs/${orgId}/join-code/rotate`,
    { method: 'POST' },
  );
  return { joinCode: data.join_code };
}

export async function addDomain(
  orgId: string,
  domain: string,
): Promise<{ dnsToken: string }> {
  const data = await apiFetch<{ txt_token: string }>(`/v1/orgs/${orgId}/domains`, {
    method: 'POST',
    body: JSON.stringify({ domain }),
  });
  return { dnsToken: data.txt_token };
}
